import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { iconFor } from "@/lib/navIcons";

function fieldInput(field, value, onChange) {
  const common = { value: value ?? "", onChange: (e) => onChange(e.target.value), placeholder: field.placeholder || "" };
  if (field.type === "textarea") return <Textarea rows={3} {...common} />;
  if (field.type === "number") return <Input type="number" {...common} />;
  if (field.type === "date") return <Input type="date" {...common} />;
  if (field.type === "email") return <Input type="email" {...common} />;
  if (field.type === "url") return <Input type="url" {...common} />;
  if (field.type === "toggle") return (
    <Switch checked={!!value} onCheckedChange={(v) => onChange(v)} />
  );
  if (field.type === "select") return (
    <Select value={value || ""} onValueChange={(v) => onChange(v)}>
      <SelectTrigger><SelectValue placeholder={field.placeholder || "Select…"} /></SelectTrigger>
      <SelectContent>{(field.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  return <Input {...common} />;
}

function RecordDialog({ open, onOpenChange, module, initial, onSave }) {
  const [data, setData] = useState({});
  useEffect(() => { setData(initial ? Object.assign({}, initial.data || {}) : {}); }, [initial, open]);
  const setField = (k, v) => setData(Object.assign({}, data, { [k]: v }));
  const submit = async () => {
    for (const f of module.fields) {
      if (f.required && !data[f.key]) return toast.error(`“${f.label}” is required`);
    }
    try { await onSave(data); onOpenChange(false); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || "Save failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="module-record-dialog">
        <DialogHeader><DialogTitle>{initial ? "Edit" : "New"} {module.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {module.fields.map((f) => (
            <div key={f.key}>
              <Label className="text-xs">{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
              {fieldInput(f, data[f.key], (v) => setField(f.key, v))}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="module-record-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ModuleRunner() {
  const { slug } = useParams();
  const [module, setModule] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, r] = await Promise.all([
        api.get(`/modules/${slug}`),
        api.get(`/modules/${slug}/records`),
      ]);
      setModule(m.data); setRecords(r.data);
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || "Module unavailable");
      setModule(null);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [slug]);

  const save = async (data) => {
    if (editing) {
      await api.put(`/modules/${slug}/records/${editing.id}`, data);
      toast.success("Record updated");
    } else {
      await api.post(`/modules/${slug}/records`, data);
      toast.success("Record created");
    }
    load();
  };

  const remove = async (rec) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/modules/${slug}/records/${rec.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  if (loading) return <div className="p-8 text-muted-foreground text-sm">Loading module…</div>;
  if (!module) return (
    <div className="p-8">
      <Link to="/" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>
      <p className="mt-4 text-sm">This module is not available.</p>
    </div>
  );

  const Ic = iconFor(module.icon);
  const columns = module.list_columns && module.list_columns.length
    ? module.list_columns
    : module.fields.slice(0, 4).map((f) => f.key);
  const colFields = columns.map((k) => module.fields.find((f) => f.key === k)).filter(Boolean);

  return (
    <div className="space-y-6" data-testid={`module-page-${slug}`}>
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="overline text-muted-foreground mb-2 flex items-center gap-2">
            <Ic size={12} weight="fill" /> Custom module
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">{module.name}</h1>
          {module.description && <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{module.description}</p>}
        </div>
        <Button className="rounded-full" onClick={() => { setEditing(null); setOpen(true); }} data-testid="module-new-record">
          <Plus size={16} className="mr-2" />New {module.name.replace(/s$/, "").toLowerCase()}
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
              <tr>
                {colFields.map((f) => <th key={f.key} className="text-left p-4">{f.label}</th>)}
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {records.map((rec) => (
                <tr key={rec.id} className="border-t border-border/40" data-testid={`module-row-${rec.id}`}>
                  {colFields.map((f) => (
                    <td key={f.key} className="p-4">{formatValue(f, (rec.data || {})[f.key])}</td>
                  ))}
                  <td className="p-4">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => { setEditing(rec); setOpen(true); }} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Edit">
                        <PencilSimple size={14} weight="duotone" />
                      </button>
                      <button onClick={() => remove(rec)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Delete">
                        <Trash size={14} weight="duotone" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!records.length && (
                <tr><td colSpan={colFields.length + 1} className="p-10 text-center text-muted-foreground">
                  No {module.name.toLowerCase()} yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <RecordDialog open={open} onOpenChange={setOpen} module={module} initial={editing} onSave={save} />
    </div>
  );
}

function formatValue(field, v) {
  if (v === null || v === undefined || v === "") return <span className="text-muted-foreground">—</span>;
  if (field.type === "toggle") return v ? "Yes" : "No";
  if (field.type === "date") return new Date(v).toLocaleDateString("en-IN");
  if (field.type === "url") return <a href={v} target="_blank" rel="noreferrer" className="text-primary hover:underline">{v}</a>;
  return String(v);
}
